import { useEffect, useState } from 'react';
import { formatPortalDateTime, type TimeDisplayMode } from '../utils/datetime';

type KeywordEventItem = {
  eventId: string;
  createdAt: string;
  channel: 'SMS' | 'MMS';
  keyword: string;
  actionType: string;
  sourceType: string;
  fromNumber: string;
  toNumber: string;
  campaignId: string | null;
};

type KeywordEventDetail = {
  Event: KeywordEventItem & {
    scopeType?: string;
    scopeId?: string;
    triggerMessageId?: string | null;
    complianceEventId?: string | null;
  };
  Actions: Array<{
    actionId: string;
    actionType: string;
    status: string;
    updatedAt: string;
  }>;
};

export function KeywordsRoute({ timeDisplayMode }: { timeDisplayMode: TimeDisplayMode }) {
  const [loading, setLoading] = useState<boolean>(false);
  const [items, setItems] = useState<KeywordEventItem[]>([]);
  const [selected, setSelected] = useState<KeywordEventDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/keyword-events?pageSize=100', { credentials: 'include' });
        if (!response.ok) {
          throw new Error(`Request failed (${response.status})`);
        }
        const payload = (await response.json()) as { Data?: { Items?: KeywordEventItem[] } };
        setItems(payload.Data?.Items ?? []);
      } catch (loadError) {
        setItems([]);
        setError(loadError instanceof Error ? loadError.message : String(loadError));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function openDetail(eventId: string): Promise<void> {
    setSelected(null);
    try {
      const response = await fetch(`/api/keyword-events/${encodeURIComponent(eventId)}`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }
      const payload = (await response.json()) as { Data?: KeywordEventDetail };
      setSelected(payload.Data ?? null);
    } catch (detailError) {
      setError(detailError instanceof Error ? detailError.message : String(detailError));
    }
  }

  return (
    <>
      <div className="page-pretitle">Compliance</div>
      <h2 className="page-title mb-3">Keywords</h2>
      {error ? <div className="alert alert-danger">{error}</div> : null}
      <div className="card mb-3">
        <div className="card-header">
          <h3 className="card-title mb-0">Recent Keyword Events</h3>
        </div>
        <div className="table-responsive">
          <table className="table card-table table-vcenter">
            <thead>
              <tr>
                <th>Detected At</th>
                <th>Keyword</th>
                <th>Channel</th>
                <th>From</th>
                <th>To</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length < 1 ? (
                <tr>
                  <td colSpan={6} className="text-secondary">
                    No keyword events found.
                  </td>
                </tr>
              ) : null}
              {items.map((item) => (
                <tr key={item.eventId} role="button" onClick={() => void openDetail(item.eventId)}>
                  <td>{formatPortalDateTime(item.createdAt, timeDisplayMode)}</td>
                  <td className="text-uppercase">{item.keyword}</td>
                  <td>{item.channel}</td>
                  <td>{item.fromNumber}</td>
                  <td>{item.toNumber}</td>
                  <td>{item.actionType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {selected?.Event ? (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title mb-0">Event Detail</h3>
          </div>
          <div className="card-body">
            <div><strong>Event ID:</strong> {selected.Event.eventId}</div>
            <div><strong>Trigger Message:</strong> {selected.Event.triggerMessageId ?? '—'}</div>
            <div><strong>Compliance Event:</strong> {selected.Event.complianceEventId ?? '—'}</div>
            <hr />
            <h4 className="h5">Actions</h4>
            <ul className="mb-0">
              {(selected.Actions ?? []).map((action) => (
                <li key={action.actionId}>
                  {action.actionType} — {action.status} ({formatPortalDateTime(action.updatedAt, timeDisplayMode)})
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </>
  );
}
